import {isBelarusRegistrationOksm, isDomesticRegistrationOksm} from './countries';

const PASSPORT_SERIES_LENGTH = 4;
const PASSPORT_NUMBER_LENGTH = 6;
const DEPARTMENT_CODE_LENGTH = 6;
const DATE_LENGTH = 8;
const BY_IDENTIFICATION_NUMBER_LENGTH = 14;
const BY_PASSPORT_LETTERS_LENGTH = 2;
const BY_PASSPORT_DIGITS_LENGTH = 7;

/**
 * Адрес регистрации на экране паспорта РФ — при резидентстве РФ или Беларуси.
 * «Адрес регистрации в РФ» на экране иностранного документа — гражданство не РФ и резидентство РФ.
 */
export const requiresPassportRegistrationAddress = (
	residenceOksmCode?: string | null,
	citizenshipOksmCode?: string | null,
): boolean => {
	if (isBelarusRegistrationOksm(citizenshipOksmCode)) {
		return false;
	}

	return isDomesticRegistrationOksm(residenceOksmCode);
};

/** Идентификационный номер и латиница — только для паспорта Беларуси. */
export const requiresPassportIdentificationNumber = (
	citizenshipOksmCode?: string | null,
): boolean => {
	return isBelarusRegistrationOksm(citizenshipOksmCode);
};

export const requiresPassportLatinNames = (citizenshipOksmCode?: string | null): boolean => {
	return isBelarusRegistrationOksm(citizenshipOksmCode);
};

/** Беларусь: номер `AA0000000`, без кода подразделения РФ. */
export const usesBelarusPassportNumber = (citizenshipOksmCode?: string | null): boolean => {
	return isBelarusRegistrationOksm(citizenshipOksmCode);
};

export const requiresPassportDepartmentCode = (citizenshipOksmCode?: string | null): boolean => {
	return !isBelarusRegistrationOksm(citizenshipOksmCode);
};

/** Беларусь: идентификационный номер — буквы и цифры, 14 символов. */
export const normalizePassportIdentificationNumber = (value: string): string => {
	return value
		.replace(/[^0-9a-zA-Zа-яА-ЯёЁ]/g, '')
		.toUpperCase()
		.slice(0, BY_IDENTIFICATION_NUMBER_LENGTH);
};

export const isPassportIdentificationNumberValid = (value: string): boolean => {
	return normalizePassportIdentificationNumber(value).length === BY_IDENTIFICATION_NUMBER_LENGTH;
};

/** Беларусь: 2 буквы + 7 цифр (`AA0000000`). */
export const normalizeBelarusPassportNumber = (value: string): string => {
	const cleaned = value.replace(/\s+/g, '').toUpperCase();
	let letters = '';
	let digits = '';

	for (const char of cleaned) {
		if (letters.length < BY_PASSPORT_LETTERS_LENGTH) {
			if (/[A-ZА-ЯЁ]/.test(char)) {
				letters += char;
			}

			continue;
		}

		if (digits.length < BY_PASSPORT_DIGITS_LENGTH && /\d/.test(char)) {
			digits += char;
		}
	}

	return `${letters}${digits}`;
};

export const formatBelarusPassportNumberDisplay = (value: string): string => {
	return normalizeBelarusPassportNumber(value);
};

export const isBelarusPassportNumberValid = (value: string): boolean => {
	const normalized = normalizeBelarusPassportNumber(value);

	return (
		normalized.length === BY_PASSPORT_LETTERS_LENGTH + BY_PASSPORT_DIGITS_LENGTH &&
		/^[A-ZА-ЯЁ]{2}\d{7}$/.test(normalized)
	);
};

export type PassportPhotoSlot = 'identity' | 'registration';

export type PassportPhotosStepValues = {
	identityPhoto: File | null;
	identityPreviewUrl: string | null;
	registrationPhoto: File | null;
	registrationPreviewUrl: string | null;
};

export type PassportDetailsStepValues = {
	seriesNumber: string;
	/** Беларусь: идентификационный номер (14 символов). */
	identificationNumber: string;
	/** Беларусь: ФИО на латинице. */
	secondNameLatin: string;
	latinName: string;
	issuer: string;
	issuedAt: string;
	departmentCode: string;
	birthDate: string;
	birthPlace: string;
	registrationAddress: string;
};

export const EMPTY_PASSPORT_PHOTOS: PassportPhotosStepValues = {
	identityPhoto: null,
	identityPreviewUrl: null,
	registrationPhoto: null,
	registrationPreviewUrl: null,
};

export const EMPTY_PASSPORT_DETAILS: PassportDetailsStepValues = {
	seriesNumber: '',
	identificationNumber: '',
	secondNameLatin: '',
	latinName: '',
	issuer: '',
	issuedAt: '',
	departmentCode: '',
	birthDate: '',
	birthPlace: '',
	registrationAddress: '',
};

export const PASSPORT_PHOTO_ACCEPT = 'image/jpeg,image/png';

const PASSPORT_PHOTO_TYPES = new Set(['image/jpeg', 'image/png']);

export const isPassportPhotoMimeType = (mimeType: string): boolean => {
	return PASSPORT_PHOTO_TYPES.has(mimeType);
};

export const isPassportPhotoFile = (file: File): boolean => {
	return isPassportPhotoMimeType(file.type);
};

export const isPassportPhotoDataTransfer = (dataTransfer: DataTransfer): boolean => {
	const fileItems = Array.from(dataTransfer.items).filter((item) => item.kind === 'file');

	if (fileItems.length > 0) {
		return isPassportPhotoMimeType(fileItems[0]?.type ?? '');
	}

	return Array.from(dataTransfer.types).some((type) => isPassportPhotoMimeType(type));
};

export const extractPassportSeriesNumberDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, PASSPORT_SERIES_LENGTH + PASSPORT_NUMBER_LENGTH);
};

export const formatPassportSeriesNumberDisplay = (digits: string): string => {
	const normalized = extractPassportSeriesNumberDigits(digits);

	if (!normalized) {
		return '';
	}

	const series = normalized.slice(0, PASSPORT_SERIES_LENGTH);
	const number = normalized.slice(PASSPORT_SERIES_LENGTH);

	if (!number) {
		return series;
	}

	return `${series} ${number}`;
};

export const splitPassportSeriesNumber = (
	digits: string,
): {series: string; number: string} | null => {
	const normalized = extractPassportSeriesNumberDigits(digits);

	if (normalized.length !== PASSPORT_SERIES_LENGTH + PASSPORT_NUMBER_LENGTH) {
		return null;
	}

	return {
		series: normalized.slice(0, PASSPORT_SERIES_LENGTH),
		number: normalized.slice(PASSPORT_SERIES_LENGTH),
	};
};

export const extractDepartmentCodeDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, DEPARTMENT_CODE_LENGTH);
};

export const formatDepartmentCodeDisplay = (digits: string): string => {
	const normalized = extractDepartmentCodeDigits(digits);

	if (!normalized) {
		return '';
	}

	const left = normalized.slice(0, 3);
	const right = normalized.slice(3);

	if (!right) {
		return left;
	}

	return `${left}-${right}`;
};

export const extractDateDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, DATE_LENGTH);
};

export const formatDateDisplay = (digits: string): string => {
	const normalized = extractDateDigits(digits);

	if (!normalized) {
		return '';
	}

	const day = normalized.slice(0, 2);
	const month = normalized.slice(2, 4);
	const year = normalized.slice(4);

	if (!month) {
		return day;
	}

	if (!year) {
		return `${day}.${month}`;
	}

	return `${day}.${month}.${year}`;
};

export const formatDateDigitsForApi = (digits: string): string => {
	const normalized = extractDateDigits(digits);

	if (normalized.length !== DATE_LENGTH) {
		return '';
	}

	const day = normalized.slice(0, 2);
	const month = normalized.slice(2, 4);
	const year = normalized.slice(4);

	return `${year}-${month}-${day}`;
};

export const isDateDigitsComplete = (digits: string): boolean => {
	return extractDateDigits(digits).length === DATE_LENGTH;
};

/** Парсит DDMMYYYY в календарную дату или null, если дата не существует. */
export const parseDateDigits = (digits: string): Date | null => {
	const normalized = extractDateDigits(digits);

	if (normalized.length !== DATE_LENGTH) {
		return null;
	}

	const day = Number(normalized.slice(0, 2));
	const month = Number(normalized.slice(2, 4));
	const year = Number(normalized.slice(4));

	if (month < 1 || month > 12 || day < 1 || year < 1) {
		return null;
	}

	const date = new Date(year, month - 1, day);

	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}

	return date;
};

const startOfLocalDay = (date: Date): Date => {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/** Дата выдачи: существует и не позже сегодняшнего дня. */
export const isIssuedAtDateValid = (digits: string, today: Date = new Date()): boolean => {
	const date = parseDateDigits(digits);

	if (!date) {
		return false;
	}

	return startOfLocalDay(date).getTime() <= startOfLocalDay(today).getTime();
};

/**
 * Дата рождения: существует, и возраст строго больше 14 лет
 * (разница в днях больше, чем 14 полных лет от этой даты).
 */
export const isBirthDateValid = (digits: string, today: Date = new Date()): boolean => {
	const birthDate = parseDateDigits(digits);

	if (!birthDate) {
		return false;
	}

	const fourteenthBirthday = new Date(
		birthDate.getFullYear() + 14,
		birthDate.getMonth(),
		birthDate.getDate(),
	);

	return startOfLocalDay(today).getTime() > startOfLocalDay(fourteenthBirthday).getTime();
};

const INVALID_DATE_ERROR = 'Проверьте дату — такой не существует';

/**
 * Сообщение об ошибке для даты выдачи документа.
 * Показывается только когда дата введена полностью (8 цифр).
 */
export const getIssuedAtDateError = (digits: string, today: Date = new Date()): string | null => {
	if (!isDateDigitsComplete(digits)) {
		return null;
	}

	if (!parseDateDigits(digits)) {
		return INVALID_DATE_ERROR;
	}

	if (!isIssuedAtDateValid(digits, today)) {
		return 'Дата выдачи не может быть в будущем';
	}

	return null;
};

/**
 * Сообщение об ошибке для даты рождения.
 * Показывается только когда дата введена полностью (8 цифр).
 */
export const getBirthDateError = (digits: string, today: Date = new Date()): string | null => {
	if (!isDateDigitsComplete(digits)) {
		return null;
	}

	if (!parseDateDigits(digits)) {
		return INVALID_DATE_ERROR;
	}

	if (!isBirthDateValid(digits, today)) {
		return 'Возраст должен быть больше 14 лет';
	}

	return null;
};

/**
 * Сообщение об ошибке для произвольной даты (например, срока действия).
 * Показывается только когда дата введена полностью (8 цифр).
 */
export const getDateExistsError = (digits: string): string | null => {
	if (!isDateDigitsComplete(digits)) {
		return null;
	}

	return parseDateDigits(digits) ? null : INVALID_DATE_ERROR;
};

export const isPassportPhotosStepValid = (values: PassportPhotosStepValues): boolean => {
	return Boolean(values.identityPhoto && values.registrationPhoto);
};

export const isPassportDetailsStepValid = (
	values: PassportDetailsStepValues,
	options?: {
		requireRegistrationAddress?: boolean;
		requireIdentificationNumber?: boolean;
		requireLatinNames?: boolean;
		requireDepartmentCode?: boolean;
		belarusPassportNumber?: boolean;
	},
): boolean => {
	const departmentCode = extractDepartmentCodeDigits(values.departmentCode);
	const requireRegistrationAddress = options?.requireRegistrationAddress !== false;
	const requireIdentificationNumber = options?.requireIdentificationNumber === true;
	const requireLatinNames = options?.requireLatinNames === true;
	const requireDepartmentCode = options?.requireDepartmentCode !== false;
	const belarusPassportNumber = options?.belarusPassportNumber === true;

	const passportNumberValid = belarusPassportNumber
		? isBelarusPassportNumberValid(values.seriesNumber)
		: extractPassportSeriesNumberDigits(values.seriesNumber).length ===
			PASSPORT_SERIES_LENGTH + PASSPORT_NUMBER_LENGTH;

	return (
		passportNumberValid &&
		(!requireIdentificationNumber || isPassportIdentificationNumberValid(values.identificationNumber)) &&
		(!requireLatinNames ||
			(values.secondNameLatin.trim().length > 0 && values.latinName.trim().length > 0)) &&
		values.issuer.trim().length > 0 &&
		isIssuedAtDateValid(values.issuedAt) &&
		(!requireDepartmentCode || departmentCode.length === DEPARTMENT_CODE_LENGTH) &&
		isBirthDateValid(values.birthDate) &&
		values.birthPlace.trim().length > 0 &&
		(!requireRegistrationAddress || values.registrationAddress.trim().length > 0)
	);
};

export const buildPassportPhotosFormData = (values: PassportPhotosStepValues): FormData => {
	const formData = new FormData();

	if (values.identityPhoto) {
		formData.append('passportPhoto', values.identityPhoto);
	}

	if (values.registrationPhoto) {
		formData.append('passportRegistrationPhoto', values.registrationPhoto);
	}

	return formData;
};

/** API: `YYYY-MM-DD` (иногда с временем) или `DD.MM.YYYY` → цифры `DDMMYYYY`. */
export const parseApiDateToDigits = (value: string | null | undefined): string => {
	const raw = value?.trim() ?? '';
	const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

	if (isoMatch) {
		const [, year, month, day] = isoMatch;
		return `${day}${month}${year}`;
	}

	return extractDateDigits(raw);
};

type ApiIdentificationDocument = {
	type?: string | null;
	series?: string | null;
	number?: string | null;
	issuer?: string | null;
	issuedAt?: string | null;
	departmentCode?: string | null;
	birthDate?: string | null;
	birthPlace?: string | null;
	registrationAddress?: string | null;
	identificationNumber?: string | null;
	personalNumber?: string | null;
	secondNameLatin?: string | null;
	latinName?: string | null;
};

/** GET /identification/info → значения шага «Введите паспортные данные» (РФ / Беларусь). */
export const mapIdentificationDocumentToPassportDetails = (
	identificationDocument: unknown,
): Partial<PassportDetailsStepValues> => {
	if (!identificationDocument || typeof identificationDocument !== 'object') {
		return {};
	}

	const document = identificationDocument as ApiIdentificationDocument;

	if (document.type !== 'russian' && document.type !== 'belarusian') {
		return {};
	}

	const identificationNumber = normalizePassportIdentificationNumber(
		document.identificationNumber ?? document.personalNumber ?? '',
	);

	const seriesNumber =
		document.type === 'belarusian'
			? normalizeBelarusPassportNumber(document.number ?? '')
			: extractPassportSeriesNumberDigits(`${document.series ?? ''}${document.number ?? ''}`);

	const candidates: PassportDetailsStepValues = {
		seriesNumber,
		identificationNumber,
		secondNameLatin: document.secondNameLatin?.trim() ?? '',
		latinName: document.latinName?.trim() ?? '',
		issuer: document.issuer?.trim() ?? '',
		issuedAt: parseApiDateToDigits(document.issuedAt),
		departmentCode: extractDepartmentCodeDigits(document.departmentCode ?? ''),
		birthDate: parseApiDateToDigits(document.birthDate),
		birthPlace: document.birthPlace?.trim() ?? '',
		registrationAddress: document.registrationAddress?.trim() ?? '',
	};

	return Object.fromEntries(
		Object.entries(candidates).filter(([, value]) => value.length > 0),
	) as Partial<PassportDetailsStepValues>;
};

export const buildPassportDocumentRequest = (
	values: PassportDetailsStepValues,
	citizenshipOksmCode?: string | null,
): {
	identificationDocument: {
		type: 'russian' | 'belarusian';
		series?: string;
		number: string;
		issuer: string;
		issuedAt: string;
		departmentCode?: string;
		birthDate: string;
		birthPlace: string;
		registrationAddress: string;
		identificationNumber?: string;
		secondNameLatin?: string;
		latinName?: string;
	};
} | null => {
	const isBelarus = isBelarusRegistrationOksm(citizenshipOksmCode);
	const identificationNumber = normalizePassportIdentificationNumber(values.identificationNumber);
	const secondNameLatin = values.secondNameLatin.trim();
	const latinName = values.latinName.trim();

	if (isBelarus) {
		const number = normalizeBelarusPassportNumber(values.seriesNumber);

		if (!isBelarusPassportNumberValid(number)) {
			return null;
		}

		if (identificationNumber.length !== BY_IDENTIFICATION_NUMBER_LENGTH) {
			return null;
		}

		if (!secondNameLatin || !latinName) {
			return null;
		}

		return {
			identificationDocument: {
				type: 'belarusian',
				number,
				issuer: values.issuer.trim(),
				issuedAt: formatDateDigitsForApi(values.issuedAt),
				birthDate: formatDateDigitsForApi(values.birthDate),
				birthPlace: values.birthPlace.trim(),
				registrationAddress: values.registrationAddress.trim(),
				identificationNumber,
				secondNameLatin,
				latinName,
			},
		};
	}

	const split = splitPassportSeriesNumber(values.seriesNumber);

	if (!split) {
		return null;
	}

	return {
		identificationDocument: {
			type: 'russian',
			series: split.series,
			number: split.number,
			issuer: values.issuer.trim(),
			issuedAt: formatDateDigitsForApi(values.issuedAt),
			departmentCode: extractDepartmentCodeDigits(values.departmentCode),
			birthDate: formatDateDigitsForApi(values.birthDate),
			birthPlace: values.birthPlace.trim(),
			registrationAddress: values.registrationAddress.trim(),
		},
	};
};
